/**
 * HeuteRenewalsBlock — prominent Fälligkeiten block for the Heute dashboard (Story R4).
 *
 * Data (V10-J): useHeuteRenewals() — live GET /renewals/heute; mock fallback
 * (MOCK_HEUTE_RENEWALS) behind the data-mode flag. The optional `data` prop
 * still overrides (tests / empty-state branch).
 *
 * Colour discipline (K4 / §47):
 *   - ONLY urgency carries sem-* colour: the "davon rot" fragment + per-row "Rot" badge + FaelligText tone.
 *   - All chrome (heading, icon, link, Kunde/Versicherer/Sparte/Prämie) stays neutral.
 *
 * [Source: docs/concept/2026-06-26_post-stefan-roadmap-and-module-specs.md §39/§45 (R4)]
 */

import { RefreshCw, ArrowRight } from 'lucide-react';
import { Link } from '@tanstack/react-router';
import type { RenewalHeuteDto, RenewalBoardItemDto } from '@brokly/shared';
import { BUCKET_BOUNDARY_DAYS } from '@brokly/shared';
import { Badge } from '../../../components/ui/Badge';
import { SparteBadge } from '../../entity-detail/components/SparteBadge';
import { FaelligText } from '../../entity-detail/components/FaelligText';
import { chf } from '../../../lib/format';
import {
  deriveHeuteRenewalsSummary,
  formatHeuteSentence,
} from '../heute-renewals-mock';
import type { HeuteRenewalsSummary } from '../heute-renewals-mock';
import { useHeuteRenewals } from '../hooks/useHeuteRenewals';

// ── Sub-components ────────────────────────────────────────────────────────────

/** Renders the primary German sentence with the "davon rot …" fragment in sem-alert. */
function HeuteSentence({ summary }: { summary: HeuteRenewalsSummary }) {
  const { dueWithin30, redCount, mostUrgentDaysLeft, hasRed } = summary;

  let redTail = '';
  if (mostUrgentDaysLeft === null) {
    redTail = '';
  } else if (mostUrgentDaysLeft < 0) {
    redTail = ' (überfällig)';
  } else if (mostUrgentDaysLeft === 0) {
    redTail = ' (heute fällig)';
  } else if (mostUrgentDaysLeft === 1) {
    redTail = ' (in 1 Tag)';
  } else {
    redTail = ` (in ${mostUrgentDaysLeft} Tagen)`;
  }

  const base = `${dueWithin30} Policen werden in ${BUCKET_BOUNDARY_DAYS.le_30d} Tagen fällig`;

  return (
    <p className="text-sm text-foreground">
      {base}
      {hasRed && (
        <>
          {', '}
          <span className="text-sem-alert font-medium">
            {redCount} davon rot
          </span>
          {redTail}
        </>
      )}
      {'.'}
    </p>
  );
}

/** A single red-list row (display-only; the whole block links to /renewals). */
function RedRow({ item }: { item: RenewalBoardItemDto }) {
  return (
    <div className="flex flex-wrap items-center gap-x-3 gap-y-1 py-2">
      {/* Badge pin */}
      <Badge variant="alert">Rot</Badge>

      {/* Policy number */}
      <span className="mono-num text-sm">{item.policy_number}</span>

      {/* Kunde + Versicherer (neutral chrome) */}
      <span className="text-sm text-muted-foreground">
        {item.kunde ?? '-'}
        {item.versicherer ? ` · ${item.versicherer}` : ''}
      </span>

      {/* Sparte */}
      {item.sparte ? <SparteBadge sparte={item.sparte} /> : null}

      {/* Prämie */}
      <span className="text-sm text-muted-foreground mono-num">
        {item.praemie !== null ? chf(Number(item.praemie)) : '-'}
      </span>

      {/* Countdown — FaelligText encodes tone discipline */}
      {item.days_left !== null ? (
        <FaelligText restTage={item.days_left} />
      ) : (
        <span className="text-sm text-muted-foreground">Frist offen</span>
      )}
    </div>
  );
}

// ── Main block ────────────────────────────────────────────────────────────────

export interface HeuteRenewalsBlockProps {
  /** Optional override for the mock (used in tests for the empty-state branch). */
  data?: RenewalHeuteDto;
}

export function HeuteRenewalsBlock({ data: dataProp }: HeuteRenewalsBlockProps) {
  // V10-J: live GET /renewals/heute (mock fallback behind the data-mode flag);
  // the explicit prop still wins (tests / empty-state branch).
  const { data: fetched, isLoading, isError } = useHeuteRenewals();
  const data = dataProp ?? fetched;

  const heading = (
    <div className="mb-2 flex items-center justify-between">
      <div className="flex items-center gap-2 text-sm font-medium">
        {/* Icon neutral only — no sem-* colour */}
        <RefreshCw className="size-4 text-muted-foreground" aria-hidden />
        Renewals · Fälligkeiten
      </div>

      {/* "Alle anzeigen" link — neutral chrome */}
      <Link
        to="/renewals"
        className="flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground"
      >
        Alle Fälligkeiten anzeigen <ArrowRight className="size-3" />
      </Link>
    </div>
  );

  if (!data) {
    return (
      <div>
        {heading}
        {isLoading ? (
          <p className="text-sm text-muted-foreground">Lade Fälligkeiten …</p>
        ) : isError ? (
          <p className="text-sm text-muted-foreground" role="alert">
            Fälligkeiten konnten nicht geladen werden.
          </p>
        ) : (
          <p className="text-sm text-muted-foreground">Keine anstehenden Fälligkeiten.</p>
        )}
      </div>
    );
  }

  const summary = deriveHeuteRenewalsSummary(data);

  return (
    <div>
      {/* Block heading row — neutral chrome, icon stays muted */}
      {heading}

      {/* Content */}
      {!summary.hasAny ? (
        <p className="text-sm text-muted-foreground">Keine anstehenden Fälligkeiten.</p>
      ) : (
        <>
          {/* Primary sentence */}
          <HeuteSentence summary={summary} />

          {/* Red list — pinned items, display-only */}
          {summary.hasRed && data.redList.length > 0 && (
            <div className="mt-3 divide-y divide-border">
              {data.redList.map((item) => (
                <RedRow key={item.policy_id} item={item} />
              ))}
            </div>
          )}
        </>
      )}
    </div>
  );
}

// Re-export for convenience (so tests can import from the component path)
export { formatHeuteSentence };
